import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";
import {
  keyForSource,
  useRecordDataSource,
} from "../Dashboard/recordDataSource";

const API_BASE = BASE_API_URL_V2;

// ============= Types =============

/** Where a series' numbers came from. Caissa returns are merged into this view
 * at read time and never written into the document-extracted tables, so the
 * label is the only thing distinguishing a custodial-bank figure from one a
 * model pulled out of a PDF (#10760). */
export type PerformanceSeriesSource = "document" | "caissa";

export interface PerformanceSeries {
  id: number;
  fund_id: string | null;
  fund_name: string | null;
  return_type: string;
  time_period_type: string;
  /** Absent on responses predating #10760; treat a missing value as "document". */
  source?: PerformanceSeriesSource;
}

/** Caissa series are read-only — they sync from the custodial bank record, so
 * the UI must not offer rename / delete / edit affordances on them. */
export const isCaissaSeries = (series: PerformanceSeries): boolean =>
  series.source === "caissa";

export interface ReturnValue {
  date: string;
  value: number;
  source_document_id?: string | null;
  source_page_num?: number | null;
}

export interface ReturnResponse {
  id: number;
  date: string;
  value: number;
  source_document_id: string | null;
  source_page_num: number | null;
  is_override: boolean;
}

export interface BulkReturnUpsert {
  returns: ReturnValue[];
}

export interface PerformanceMetrics {
  series_id: number;
  return_type: string;
  one_month_return: number | null;
  quarter_to_date_return: number | null;
  year_to_date_return: number | null;
  fiscal_year_to_date_return: number | null;
  one_year_return: number | null;
  two_year_return: number | null;
  three_year_return: number | null;
  five_year_return: number | null;
  seven_year_return: number | null;
  ten_year_return: number | null;
  inception_to_date_return: number | null;
  inception_date: string | null;
  overridden_periods: string[];
}

export interface AnnualizedOverridePayload {
  period: string;
  value: number | null;
  as_of_date: string;
}

export interface CreatePerformanceSeriesPayload {
  entity_public_id: string;
  name: string;
  inception_date: string; // YYYY-MM-DD
}

export interface CreatePerformanceSeriesResponse {
  fund_id: string;
  fund_name: string;
  series_id: number;
}

// ============= API Functions =============

export const fetchPerformanceSeries = async (
  entityPublicId: string,
  organizationId?: string
): Promise<PerformanceSeries[]> => {
  const params = new URLSearchParams({
    entity_public_id: entityPublicId,
  });
  if (organizationId) params.append("organization_id", organizationId);

  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/performance/series?${params}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }
  );
  if (!response.ok) throw new Error("Failed to fetch performance series");
  return response.json();
};

export const createPerformanceSeries = async (
  payload: CreatePerformanceSeriesPayload,
  organizationId?: string
): Promise<CreatePerformanceSeriesResponse> => {
  const params = organizationId ? `?organization_id=${organizationId}` : "";
  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/performance/series${params}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.detail || "Failed to create performance series");
  }
  return response.json();
};

export const renamePerformanceSeries = async (
  fundId: string,
  name: string,
  entityPublicId: string,
  organizationId?: string
): Promise<{ ok: boolean; fund_name: string }> => {
  const params = new URLSearchParams({ entity_public_id: entityPublicId });
  if (organizationId) params.append("organization_id", organizationId);
  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/performance/series?${params}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fund_id: fundId, name }),
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.detail || "Failed to rename performance series");
  }
  return response.json();
};

export interface ReturnConflict {
  return_type: string;
  time_period_type: string;
  date: string;
  source_value: number;
  target_value: number;
  kind: "document" | "override";
}

export class ReturnConflictError extends Error {
  conflicts: ReturnConflict[];
  constructor(conflicts: ReturnConflict[]) {
    super("Return value conflicts prevent transfer");
    this.name = "ReturnConflictError";
    this.conflicts = conflicts;
  }
}

const CONFLICT_PREFIX = "return_conflicts:";

export const deletePerformanceSeries = async (
  fundId: string,
  entityPublicId: string,
  organizationId?: string,
  transferToFundId?: string
): Promise<{ ok: boolean }> => {
  const params = new URLSearchParams({ entity_public_id: entityPublicId });
  if (organizationId) params.append("organization_id", organizationId);
  const body: { fund_id: string; transfer_to_fund_id?: string } = {
    fund_id: fundId,
  };
  if (transferToFundId) body.transfer_to_fund_id = transferToFundId;
  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/performance/series?${params}`,
    {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    const detail: string = typeof err?.detail === "string" ? err.detail : "";
    if (detail.startsWith(CONFLICT_PREFIX)) {
      const payload = JSON.parse(detail.slice(CONFLICT_PREFIX.length));
      throw new ReturnConflictError(payload.conflicts ?? []);
    }
    throw new Error(detail || "Failed to delete performance series");
  }
  return response.json();
};

export const fetchPerformanceReturns = async (
  seriesId: number,
  organizationId?: string
): Promise<ReturnResponse[]> => {
  const params = organizationId ? `?organization_id=${organizationId}` : "";
  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/performance/series/${seriesId}/returns${params}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }
  );
  if (!response.ok) throw new Error("Failed to fetch performance returns");
  return response.json();
};

export const bulkUpsertReturns = async (
  seriesId: number,
  payload: BulkReturnUpsert,
  organizationId?: string
): Promise<{ ok: boolean; upserted: number }> => {
  const params = organizationId ? `?organization_id=${organizationId}` : "";
  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/performance/series/${seriesId}/returns${params}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) throw new Error("Failed to upsert performance returns");
  return response.json();
};

export const deletePerformanceReturn = async (
  seriesId: number,
  returnDate: string,
  organizationId?: string
): Promise<{ ok: boolean }> => {
  const params = organizationId ? `?organization_id=${organizationId}` : "";
  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/performance/series/${seriesId}/returns/${returnDate}${params}`,
    {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    }
  );
  if (!response.ok) throw new Error("Failed to delete performance return");
  return response.json();
};

export const upsertAnnualizedOverride = async (
  seriesId: number,
  payload: AnnualizedOverridePayload,
  organizationId?: string
): Promise<{ ok: boolean }> => {
  const params = organizationId ? `?organization_id=${organizationId}` : "";
  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/performance/series/${seriesId}/annualized-overrides${params}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) throw new Error("Failed to upsert annualized override");
  return response.json();
};

export const fetchPerformanceMetrics = async (
  entityPublicId: string,
  returnType: string,
  organizationId?: string,
  asOfDate?: string
): Promise<PerformanceMetrics[]> => {
  const params = new URLSearchParams({
    entity_public_id: entityPublicId,
    return_type: returnType,
  });
  if (organizationId) params.append("organization_id", organizationId);
  if (asOfDate) params.append("as_of_date", asOfDate);

  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/performance/metrics?${params}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }
  );
  if (!response.ok) throw new Error("Failed to fetch performance metrics");
  return response.json();
};

// ============= Private-fund performance (#11667) =============

/** Why a private-fund figure is blank. A bare null cannot be told apart from
 * "not loaded yet", so the backend names the cause and the UI explains it
 * rather than showing an unlabelled dash. */
export type PrivateMetricsUnavailableReason =
  | "no_transactions"
  | "valuation_precedes_ledger"
  | "incomplete_amounts"
  | "unknown_currency"
  | "mixed_currency"
  | "no_valuation"
  | "no_time_span"
  | "no_sign_change"
  | "no_convergence";

export interface PeriodReturn {
  /** Calendar dates the period spans, `YYYY-MM-DD`.
   *
   * Both ends matter: the length is what tells a quarter from a month, and
   * without it a monthly-marked fund's March return would be filed under Q1 and
   * read as three months of performance. */
  starts: string;
  ends: string;
  /** Null when the period exists but could not be measured. It is still sent so
   * the reader can see it happened; the year containing it is then absent from
   * `annual_returns` rather than compounded from the periods that survived. */
  percent: number | null;
}

export interface YearReturn {
  year: number;
  percent: number;
}

export interface PrivateFundMetrics {
  entity_public_id: string;
  /** The date that was asked for. */
  as_of: string;
  /** The date the figures actually cover — the valuation's date whenever one
   * exists, which is often earlier than `as_of`. Label the numbers with this,
   * never with `as_of`: a NAV can only speak for the ledger up to its own date. */
  computed_through: string | null;
  irr: number | null;
  paid_in: number | null;
  returned: number | null;
  nav: number | null;
  nav_date: string | null;
  /** The single currency every money figure above is in. Present only when the
   * figures are — establishing the shared unit is what the server-side currency
   * guards do, so without this the client would render a EUR fund as dollars. */
  currency: string | null;
  /** A return for each period between consecutive valuations, oldest first, and
   * those periods compounded by calendar year. The factsheet shape: the IRR is
   * one rate for the whole holding, these show it period by period. Empty when
   * the summary figures are unavailable or there is no valuation at all — a
   * single mark still yields an opening period from the first cash flow to it.
   * `annual_returns` may be shorter than the years present in `period_returns`,
   * because a year holding an unmeasurable period is withheld. */
  period_returns: PeriodReturn[];
  annual_returns: YearReturn[];
  unavailable_reason: PrivateMetricsUnavailableReason | null;
}

export const fetchPrivateFundMetrics = async (
  entityPublicId: string,
  organizationId?: string,
  asOfDate?: string
): Promise<PrivateFundMetrics> => {
  const params = new URLSearchParams({ entity_public_id: entityPublicId });
  if (organizationId) params.append("organization_id", organizationId);
  if (asOfDate) params.append("as_of_date", asOfDate);

  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/performance/private-metrics?${params}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }
  );
  if (!response.ok) throw new Error("Failed to fetch private fund metrics");
  return response.json();
};

export interface ValuationPayload {
  entity_public_id: string;
  /** Calendar date, `YYYY-MM-DD`. Never a timestamp: a valuation date is a day. */
  as_of: string;
  value: string;
  currency?: string;
}

export interface Valuation {
  id: number;
  as_of: string;
  value: number;
  currency: string;
  source: string;
}

export const fetchValuations = async (
  entityPublicId: string,
  organizationId?: string
): Promise<Valuation[]> => {
  const params = new URLSearchParams({ entity_public_id: entityPublicId });
  if (organizationId) params.append("organization_id", organizationId);

  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/performance/valuations?${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  if (!response.ok) throw new Error("Failed to fetch valuations");
  return response.json();
};

export const upsertValuation = async (
  payload: ValuationPayload,
  organizationId?: string
): Promise<Valuation> => {
  const params = new URLSearchParams();
  if (organizationId) params.append("organization_id", organizationId);

  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/performance/valuations?${params}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "Failed to save the NAV");
  }
  return response.json();
};

// ============= Org-wide Performance Types =============

export interface OrgFundMetrics {
  fund_id: string;
  fund_name: string | null;
  return_type: string;
  inception_date: string | null;
  labels: string[];
  show: boolean;
  one_month_return: number | null;
  quarter_to_date_return: number | null;
  year_to_date_return: number | null;
  one_year_return: number | null;
  three_year_return: number | null;
  five_year_return: number | null;
  seven_year_return: number | null;
  ten_year_return: number | null;
  inception_to_date_return: number | null;
  overridden_periods: string[];
  entity_public_id: string | null;
  entity_name: string | null;
}

export interface OrgFundReturns {
  fund_id: string;
  fund_name: string | null;
  return_type: string;
  inception_date: string | null;
  labels: string[];
  show: boolean;
  returns: { date: string; value: number }[];
  entity_public_id: string | null;
  entity_name: string | null;
}

// ============= Performance Schema Builder =============

import type { RmsSchema, SchemaFact, EntityRow } from "../Dashboard/types";

function makePerformanceFact(
  id: string,
  name: string,
  scalarType: "string" | "number" | "date",
  opts?: { unit?: string; isArray?: boolean; isSortable?: boolean }
): SchemaFact {
  return {
    id,
    name,
    position: 0,
    inverse_name: null,
    is_system_managed: true,
    is_required: false,
    is_array: opts?.isArray ?? false,
    is_range: false,
    is_enum: false,
    is_hierarchical: false,
    ai_autofill: false,
    renew_after: null,
    scalar_type: scalarType,
    scalar_unit: opts?.unit ?? null,
    scalar_values: null,
    enum_colors: null,
    entity_types: null,
    description: "",
    default_scalar_value: null,
    default_entity_ids: null,
    is_sortable: opts?.isSortable ?? true,
  };
}

const ANNUAL_FACTS: SchemaFact[] = [
  makePerformanceFact("one_month_return", "1 Month", "number", {
    unit: "percent",
  }),
  makePerformanceFact("quarter_to_date_return", "QTD", "number", {
    unit: "percent",
  }),
  makePerformanceFact("year_to_date_return", "YTD", "number", {
    unit: "percent",
  }),
  makePerformanceFact("one_year_return", "1 YR", "number", {
    unit: "percent",
  }),
  makePerformanceFact("three_year_return", "3 YR", "number", {
    unit: "percent",
  }),
  makePerformanceFact("five_year_return", "5 YR", "number", {
    unit: "percent",
  }),
  makePerformanceFact("seven_year_return", "7 YR", "number", {
    unit: "percent",
  }),
  makePerformanceFact("ten_year_return", "10 YR", "number", {
    unit: "percent",
  }),
  makePerformanceFact("inception_to_date_return", "Since Inception", "number", {
    unit: "percent",
  }),
  makePerformanceFact("inception_date", "Inception Date", "string"),
  makePerformanceFact("labels", "Labels", "string", { isArray: true }),
];

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function buildPerformanceSchema(
  timePeriod: string,
  data?: EntityRow[],
  // Tag label → its category name; label chips derive their hue from the
  // category via the token ramp (#11426, round 5 — org hex never paints).
  labelCategoryNames?: Record<string, string>
): RmsSchema {
  if (timePeriod === "annual") {
    if (labelCategoryNames) {
      const facts = ANNUAL_FACTS.map((f) =>
        f.id === "labels" ? { ...f, category_names: labelCategoryNames } : f
      );
      return { facts };
    }
    return { facts: ANNUAL_FACTS };
  }

  // Monthly/quarterly: dynamic date columns from data
  const dates = new Set<string>();
  if (data) {
    for (const row of data) {
      for (const key of Object.keys(row.data)) {
        if (key !== "labels" && !key.startsWith("_")) dates.add(key);
      }
    }
  }
  const sortedDates = Array.from(dates).sort().reverse(); // Most recent first
  const dateFacts = sortedDates.map((dateStr) => {
    let label = dateStr;
    if (timePeriod === "monthly") {
      const d = new Date(dateStr + "T00:00:00");
      if (!isNaN(d.getTime())) {
        label = `${SHORT_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
      }
    } else if (timePeriod === "quarterly") {
      const match = dateStr.match(/^(\d{4})-Q(\d)$/);
      if (match) {
        label = `Q${match[2]}, ${match[1]}`;
      }
    }
    return makePerformanceFact(dateStr, label, "number", { unit: "percent" });
  });

  const labelsFact = makePerformanceFact("labels", "Labels", "string", {
    isArray: true,
  });
  if (labelCategoryNames) {
    labelsFact.category_names = labelCategoryNames;
  }

  return {
    facts: [...dateFacts, labelsFact],
  };
}

// ============= Fund Grouping =============

export interface FundGroup {
  key: string;
  fundName: string | null;
  series: PerformanceSeries[];
  /** True when every series in the group is Caissa-sourced. Such a group is
   * read-only: it syncs from the custodial bank record, so the UI must label it
   * and must not offer rename / delete affordances (#10760). */
  isCaissa: boolean;
}

export function groupSeriesByFund(
  seriesList: PerformanceSeries[]
): FundGroup[] {
  const map = new Map<string, FundGroup>();
  for (const s of seriesList) {
    const key = s.fund_id || String(s.id);
    if (!map.has(key)) {
      map.set(key, { key, fundName: s.fund_name, series: [], isCaissa: true });
    }
    const group = map.get(key)!;
    group.series.push(s);
    // A group is Caissa-sourced only if every series in it is. A mixed group
    // must stay editable, or a stray Caissa series would lock a fund's
    // document-extracted numbers out of editing.
    group.isCaissa = group.isCaissa && isCaissaSeries(s);
    if (!group.fundName && s.fund_name) {
      group.fundName = s.fund_name;
    }
  }
  return Array.from(map.values());
}

export function getPrimaryNetSeries(
  group: FundGroup
): PerformanceSeries | null {
  return (
    group.series.find(
      (s) => s.return_type === "net" && s.time_period_type === "monthly"
    ) ||
    group.series.find((s) => s.return_type === "net") ||
    group.series[0] ||
    null
  );
}

export function getPrimaryGrossSeries(
  group: FundGroup
): PerformanceSeries | null {
  return (
    group.series.find(
      (s) => s.return_type === "gross" && s.time_period_type === "monthly"
    ) ||
    group.series.find((s) => s.return_type === "gross") ||
    null
  );
}

// ============= Query Keys =============

export const PERFORMANCE_QUERY_KEYS = {
  all: ["performance"] as const,
  series: (entityPublicId: string) =>
    [...PERFORMANCE_QUERY_KEYS.all, "series", entityPublicId] as const,
  returns: (seriesId: number) =>
    [...PERFORMANCE_QUERY_KEYS.all, "returns", seriesId] as const,
  metrics: (entityPublicId: string, returnType: string) =>
    [
      ...PERFORMANCE_QUERY_KEYS.all,
      "metrics",
      entityPublicId,
      returnType,
    ] as const,
  privateMetrics: (entityPublicId: string, asOfDate?: string) =>
    [
      ...PERFORMANCE_QUERY_KEYS.all,
      "private-metrics",
      entityPublicId,
      asOfDate,
    ] as const,
  valuations: (entityPublicId: string) =>
    [...PERFORMANCE_QUERY_KEYS.all, "valuations", entityPublicId] as const,
};

// ============= React Query Hooks =============

export const usePerformanceSeries = (
  entityPublicId: string,
  organizationId?: string
) => {
  // A record's series, read through the record's data source (#13370, round 36 review).
  const source = useRecordDataSource();
  return useQuery({
    queryKey: keyForSource(
      source,
      PERFORMANCE_QUERY_KEYS.series(entityPublicId)
    ),
    queryFn: () => source.performanceSeries(entityPublicId, organizationId),
    enabled: !!entityPublicId,
    staleTime: 30 * 1000,
  });
};

export const useCreatePerformanceSeries = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      organizationId,
    }: {
      payload: CreatePerformanceSeriesPayload;
      organizationId?: string;
    }) => createPerformanceSeries(payload, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PERFORMANCE_QUERY_KEYS.all,
      });
    },
  });
};

export const useRenamePerformanceSeries = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      fundId,
      name,
      entityPublicId,
      organizationId,
    }: {
      fundId: string;
      name: string;
      entityPublicId: string;
      organizationId?: string;
    }) => renamePerformanceSeries(fundId, name, entityPublicId, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PERFORMANCE_QUERY_KEYS.all,
      });
    },
  });
};

export const useDeletePerformanceSeries = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      fundId,
      entityPublicId,
      organizationId,
      transferToFundId,
    }: {
      fundId: string;
      entityPublicId: string;
      organizationId?: string;
      transferToFundId?: string;
    }) =>
      deletePerformanceSeries(
        fundId,
        entityPublicId,
        organizationId,
        transferToFundId
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PERFORMANCE_QUERY_KEYS.all,
      });
    },
  });
};

export const usePerformanceReturns = (
  seriesId: number | null,
  organizationId?: string
) => {
  return useQuery({
    queryKey: PERFORMANCE_QUERY_KEYS.returns(seriesId || 0),
    queryFn: () => fetchPerformanceReturns(seriesId!, organizationId),
    enabled: !!seriesId,
    staleTime: 30 * 1000,
  });
};

export const useBulkUpsertReturns = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      seriesId,
      payload,
      organizationId,
    }: {
      seriesId: number;
      payload: BulkReturnUpsert;
      organizationId?: string;
    }) => bulkUpsertReturns(seriesId, payload, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PERFORMANCE_QUERY_KEYS.all,
      });
    },
  });
};

export const useDeletePerformanceReturn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      seriesId,
      returnDate,
      organizationId,
    }: {
      seriesId: number;
      returnDate: string;
      organizationId?: string;
    }) => deletePerformanceReturn(seriesId, returnDate, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PERFORMANCE_QUERY_KEYS.all,
      });
    },
  });
};

export const useUpsertAnnualizedOverride = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      seriesId,
      payload,
      organizationId,
    }: {
      seriesId: number;
      payload: AnnualizedOverridePayload;
      organizationId?: string;
    }) => upsertAnnualizedOverride(seriesId, payload, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PERFORMANCE_QUERY_KEYS.all,
      });
    },
  });
};

export const usePerformanceMetrics = (
  entityPublicId: string,
  returnType: string,
  organizationId?: string,
  asOfDate?: string
) => {
  const source = useRecordDataSource();
  return useQuery({
    queryKey: keyForSource(source, [
      ...PERFORMANCE_QUERY_KEYS.metrics(entityPublicId, returnType),
      asOfDate,
    ]),
    queryFn: () =>
      source.performanceMetrics(
        entityPublicId,
        returnType,
        organizationId,
        asOfDate
      ),
    enabled: !!entityPublicId,
    staleTime: 30 * 1000,
  });
};

/** IRR, Paid in and Returned for one vehicle (#11667).
 *
 * Only vehicles carry these — the backend answers 400 for a portfolio or a deal,
 * because the ledger read behind it does no roll-up — so callers gate on kind
 * rather than letting the panel fire a request that is always going to fail. */
export const usePrivateFundMetrics = (
  entityPublicId: string,
  enabled: boolean,
  organizationId?: string,
  asOfDate?: string
) => {
  const source = useRecordDataSource();
  return useQuery({
    queryKey: keyForSource(
      source,
      PERFORMANCE_QUERY_KEYS.privateMetrics(entityPublicId, asOfDate)
    ),
    queryFn: () =>
      source.privateFundMetrics(entityPublicId, organizationId, asOfDate),
    enabled: enabled && !!entityPublicId,
    staleTime: 30 * 1000,
  });
};

export const useValuations = (
  entityPublicId: string,
  enabled: boolean,
  organizationId?: string
) => {
  return useQuery({
    queryKey: PERFORMANCE_QUERY_KEYS.valuations(entityPublicId),
    queryFn: () => fetchValuations(entityPublicId, organizationId),
    enabled: enabled && !!entityPublicId,
  });
};

export const useUpsertValuation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      organizationId,
    }: {
      payload: ValuationPayload;
      organizationId?: string;
    }) => upsertValuation(payload, organizationId),
    onSuccess: () => {
      // Invalidate the whole performance tree, not just the valuations list:
      // a new mark moves the IRR, Paid in and Returned as well, and those live
      // under the private-metrics key.
      queryClient.invalidateQueries({ queryKey: PERFORMANCE_QUERY_KEYS.all });
    },
  });
};
